import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal/index';
import ClockCautionColorIcon from '@flipster/react/icon/generated/ClockCautionColorIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { getTimezoneOffset } from '@flipster/utils/datetime';

import { OffHoursBanner } from './off-hours-banner';
import { tradingSessionBunja } from './state';
import { dayTranslationKeys, formatDateDayKey, formatDatePrefix, formatTimeRange } from './utils';

export interface TradingHoursNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  showTradabilityStatus?: boolean;
}

export const TradingHoursNoticeModal = ({
  isOpen,
  onClose,
  onExit,
  showTradabilityStatus = true,
}: TradingHoursNoticeModalProps) => {
  const { isTradableAtom, weeklyTradingScheduleAtom } = useBunja(tradingSessionBunja);
  const isTradable = useAtomValue(isTradableAtom);
  const weeklySchedule = useAtomValue(weeklyTradingScheduleAtom);

  const statusContent = isTradable ? (
    <>
      <span className={css({ w: 8, h: 8, rounded: '100%', bg: 'content.state.positive', flexShrink: 0 })} />
      <span className={css({ textStyle: 'Text/M/Regular', color: 'content.state.positive' })}>
        <T id='Trade.r260318.Tradfi_trading_hours_available' />
      </span>
    </>
  ) : (
    <div
      className={hstack({
        w: '100%',
        p: 12,
        bg: 'background.default.level-2',
        gap: 10,
        rounded: 8,
      })}
    >
      <ClockCautionColorIcon size={20} />
      <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-4' })}>
        <T id='Trade.r260318.Tradfi_trading_hours_unavailable' />
      </span>
    </div>
  );

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} mode='bottomSheet'>
      <Modal.Header>
        <Modal.Title label={<T id='Trade.r260318.Tradfi_trading_hours' />} />
      </Modal.Header>
      <Modal.Content>
        {showTradabilityStatus && (
          // tint 박스 없이 점/아이콘 + 텍스트 단순 행 + off-hours 배너 (강조는 배너가 담당)
          <div className={stack({ gap: 12, mb: 16 })}>
            <div className={flex({ gap: 10, align: 'center' })}>{statusContent}</div>
            {/* TradFi 심볼이면 off-hours 상태(info/warning) 배너 표시 (비 TradFi면 null) */}
            <OffHoursBanner />
          </div>
        )}
        <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2', mb: 8 })}>
          <T id='Trade.r260318.Tradfi_trading_hours_display_time' /> ({getTimezoneOffset()})
        </div>
        {weeklySchedule.map((daily) => (
          <div
            key={daily.date.toISOString()}
            className={stack({
              gap: 4,
              py: 10,
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <div className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
              {`${formatDatePrefix(daily.date)}`} (<T id={dayTranslationKeys[formatDateDayKey(daily.date)]} />)
            </div>
            <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              {daily.windows.map(({ start, end }) => formatTimeRange(start, end)).join(', ')}
            </div>
          </div>
        ))}
        <div
          className={css({
            mt: 16,
            whiteSpace: 'pre-line',
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-2',
          })}
        >
          {showTradabilityStatus && <T id='Trade.r260318.Tradfi_trading_hours_description' />}
          {!showTradabilityStatus && <T id='Trade.r260318.Tradfi_trading_hours_disclaimer' />}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<T id='Trade.r260318.Tradfi_trading_hours_cta' />} onClick={onClose} />
      </Modal.Footer>
    </Modal.Root>
  );
};
